import "./styles.css";
import { openDb } from "./data/index.ts";
import type { Db } from "./data/db.ts";
import { isOpen, leadNextAction } from "./lib/leads.ts";
import { londonDate } from "./lib/working-days.ts";
import { auditView } from "./ui/audit.ts";
import { auditsView } from "./ui/audits.ts";
import { leadView, leadsView } from "./ui/leads.ts";
import { todayView } from "./ui/today.ts";
import { action, h, mount, notice } from "./ui/dom.ts";
import { googleMark } from "./ui/icons.ts";
import { loading } from "./ui/katana.ts";
import { openPalette, openPanel } from "./ui/palette.ts";
import { applyPrefs, onPrefs, prefs, remember, setPrefs } from "./ui/prefs.ts";
import { createShell, setAccountName, type Shell } from "./ui/shell.ts";
import { installShortcuts } from "./ui/shortcuts.ts";
import { toast } from "./ui/toast.ts";
import { LANGS, lang, langAttrs, t } from "./ui/i18n.ts";
import "./ui/sections.ts";
import { clearPopupSignIn, isPopupLanding, takeReturn } from "./data/sign-in-return.ts";

const app = document.getElementById("app")!;
applyPrefs();
// "Match computer" follows the computer when it switches between light and dark.
matchMedia("(prefers-color-scheme: dark)").addEventListener?.("change", () => applyPrefs());
// A file dropped just outside a drop zone would open in the browser and lose the page: ignore it.
for (const ev of ["dragover", "drop"]) window.addEventListener(ev, (e) => { if (!(e.target instanceof Element && e.target.closest(".drop-target"))) e.preventDefault(); });

function signInView(db: Db, message?: HTMLElement): HTMLElement {
  return h("div", { class: "signin" },
    h("section", { class: "signin-art", "aria-label": "About the platform" },
      h("div", { class: "signin-cut", "aria-hidden": "true" }, h("i", null)),
      h("img", { class: "logo", src: "/brand/logo-on-dark.svg", alt: "Ryoma Insight", width: "260", height: "146" }),
      h("div", null,
        h("h1", null, "Cut the cost. ", h("em", null, "Keep every promise.")),
        h("p", null, "Leads, audits, reports and implementation in one place. Replies within 2 working days, reports within 5 working days of complete data."),
      ),
      h("div", { class: "signin-facts" }, h("span", null, "Staff only"), h("span", null, "Google Workspace sign-in"), h("span", null, "Data held in London")),
    ),
    h("main", { class: "signin-panel" },
      h("div", { class: "signin-card", ...langAttrs() },
        h("img", { class: "mark on-light", src: "/brand/mark.svg", alt: "", width: "64", height: "64" }),
        h("img", { class: "mark on-dark", src: "/brand/mark-on-dark.svg", alt: "", width: "64", height: "64" }),
        h("h2", null, t("Sign in to the platform")),
        h("p", { class: "muted" }, t("Use your @ryoma-insight.co.uk Google account.")),
        message ?? "",
        signInButton(db),
        h("label", { class: "small muted signin-lang" }, `${t("Language")} `,
          h("select", { onChange: (e: Event) => { setPrefs({ language: (e.target as HTMLSelectElement).value }); void route(db); } },
            LANGS.map((l) => h("option", { value: l.code, selected: lang() === l.code, lang: l.tag }, l.name)))),
        h("p", { class: "muted small" }, db.kind === "memory" ? "Demo mode: sample data, kept in this browser only." : "Access is limited to the founders' allow-list."),
      ),
    ),
  );
}

function signInButton(db: Db): HTMLElement {
  const a = action(t("Sign in with Google"), async () => db.signIn(), { primary: true });
  a.querySelector("button")?.prepend(googleMark());
  return a;
}

/** Leads waiting for a reply, and everything due today or late, for the sidebar counts. */
async function counts(db: Db): Promise<{ replies: number; late: number; today: number }> {
  try {
    const leads = await db.listLeads();
    const now = new Date();
    const today = londonDate(now);
    const open = leads.filter((l) => isOpen(l.status));
    const replies = open.filter((l) => l.status === "new");
    const late = replies.filter((l) => leadNextAction(l, now)?.overdue).length;
    const due = open.filter((l) => {
      const n = leadNextAction(l, now);
      return n && (n.overdue || (n.due && n.due.slice(0, 10) <= today));
    }).length;
    return { replies: replies.length, late, today: due };
  } catch {
    return { replies: 0, late: 0, today: 0 };
  }
}

let shell: Shell | null = null;
let shellFor = "";
let first = true;

async function route(db: Db): Promise<void> {
  const user = await db.currentUser();
  if (!user) {
    shell = null;
    mount(app, signInView(db));
    document.title = "Sign in · Ryoma";
    return;
  }
  if (!(await db.isStaff())) {
    const email = user.email;
    await db.signOut();
    mount(app, signInView(db, notice("error", `${email} does not have access. Ask a founder to add you.`)));
    return;
  }
  if (!shell || shellFor !== user.email) {
    setAccountName(user.name);
    shell = createShell(db, user.email);
    shellFor = user.email;
    first = true;
    mount(app, shell.frame);
    // Back to the page you were on before going to Google.
    const back = takeReturn();
    if (back && back !== location.hash) {
      location.hash = back;
      return;
    }
    // Open on the chosen start page after signing in.
    if ((!location.hash || location.hash === "#/") && prefs().startPage !== "#/") {
      location.hash = prefs().startPage;
      return;
    }
  }
  const hash = location.hash || "#/";
  const path = hash.split("?")[0]!;
  let view: HTMLElement;
  loading(true);
  try {
    const m = /^#\/audit\/([\w-]+)$/.exec(path);
    const lead = /^#\/lead\/([\w-]+)$/.exec(path);
    if (m) view = await auditView(db, m[1]!);
    else if (lead) view = await leadView(db, lead[1]!);
    else if (path.startsWith("#/leads")) view = await leadsView(db);
    else if (path.startsWith("#/timeline")) view = await (await import("./ui/timeline.ts")).timelineView(db);
    else if (path.startsWith("#/insights")) view = await (await import("./ui/insights.ts")).insightsView(db);
    else if (path.startsWith("#/pipeline")) view = await (await import("./ui/pipeline.ts")).pipelineView(db);
    else if (path.startsWith("#/partners")) view = await (await import("./ui/partners.ts")).partnersView(db);
    else if (path.startsWith("#/audits")) view = await auditsView(db);
    else if (path.startsWith("#/reference")) view = await (await import("./ui/reference.ts")).referenceView(db);
    else if (path.startsWith("#/tools")) view = (await import("./ui/tools.ts")).toolsView();
    else if (path.startsWith("#/documents")) view = await (await import("./ui/documents.ts")).documentsView(db);
    else if (path.startsWith("#/activity")) view = await (await import("./ui/activity.ts")).activityView(db);
    else if (path.startsWith("#/settings")) view = await (await import("./ui/settings.ts")).settingsView(db, user.email);
    else view = await todayView(db, user.email);
  } catch (e) {
    view = notice("error", e instanceof Error ? e.message : String(e));
  } finally {
    loading(false);
  }
  if (location.hash && location.hash !== hash) return; // moved on while this page loaded
  shell.show(view, path, first);
  first = false;
  const title = view.querySelector("h1")?.textContent?.trim() ?? "";
  if (/^#\/(lead|audit)\//.test(path) && title) {
    shell.setDetail(title);
    remember({ href: path, title, kind: path.startsWith("#/lead/") ? "lead" : "audit" });
  }
  if (hash.endsWith("?add")) {
    history.replaceState(null, "", path);
    openPanel("Add a prospect or lead");
  }
  window.dispatchEvent(new Event("ryoma:routed"));
  const c = await counts(db);
  shell?.setCounts(c);
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
let sessionDialog: HTMLDialogElement | null = null;
let checkingSession = false;

/**
 * Sign-in changed. The page is redrawn only when someone else signs in. When the session seems to
 * end (often only for a moment, while it is renewed after the tab or computer slept), the page is
 * left exactly as it is: we wait, try to renew, and only if that fails ask you to sign in again in a
 * small window, so whatever you were typing is still there afterwards.
 */
async function onAuth(db: Db, byChoice: boolean): Promise<void> {
  const u = await db.currentUser();
  if (shell && u && u.email === shellFor) { closeSessionDialog(); return; }
  if (shell && !u && !byChoice) {
    if (checkingSession) return;
    checkingSession = true;
    try {
      for (const wait of [500, 1500, 3000]) {
        await sleep(wait);
        const back = (await db.currentUser()) ?? (await db.recoverSession());
        if (back?.email === shellFor) return;
        if (back) break;
      }
    } finally {
      checkingSession = false;
    }
    const now = await db.currentUser();
    if (!now) { openSessionDialog(db); return; }
    if (now.email === shellFor) return;
  }
  closeSessionDialog();
  void route(db);
}

function openSessionDialog(db: Db): void {
  if (sessionDialog?.open) return;
  sessionDialog?.remove();
  const signIn = action(t("Sign in with Google"), async () => db.signIn({ popup: true }), { primary: true });
  signIn.querySelector("button")?.prepend(googleMark());
  sessionDialog = h("dialog", { class: "modal", "aria-labelledby": "session-title", ...langAttrs() },
    h("div", { class: "modal-head" }, h("h2", { id: "session-title" }, "Please sign in again")),
    h("div", { class: "modal-body" },
      h("p", null, "Your session has ended. Sign in to carry on: this page stays as it is, with anything you typed."),
      h("div", { class: "row-actions" }, signIn)),
  );
  // Escape would leave you on a page that cannot save; keep the dialog until you are back.
  sessionDialog.addEventListener("cancel", (e) => e.preventDefault());
  document.body.append(sessionDialog);
  sessionDialog.showModal();
}

function closeSessionDialog(): void {
  if (!sessionDialog) return;
  if (sessionDialog.open) { sessionDialog.close(); toast("Signed in again"); }
  sessionDialog.remove();
  sessionDialog = null;
}

async function start(): Promise<void> {
  // The small sign-in window: once Google hands back, finish signing in and close, leaving the page underneath alone.
  const popupLanding = isPopupLanding(location.search);
  try {
    const db = await openDb();
    // Demo and tests only: lets the browser tests simulate a session that lapses.
    if (import.meta.env.DEV) (window as unknown as { __ryomaDb: Db }).__ryomaDb = db;
    if (popupLanding) {
      const u = await db.currentUser();
      clearPopupSignIn();
      mount(app, h("main", { class: "page" }, notice(u ? "ok" : "error", u ? "Signed in. This window closes now; carry on in the platform." : "Sign-in did not finish. Close this window and try again.")));
      if (u) setTimeout(() => window.close(), 800);
      return;
    }
    window.addEventListener("hashchange", () => void route(db));
    installShortcuts({
      palette: () => { if (shell) openPalette(db); },
      newLead: () => { if (shell) location.hash = "#/leads?add"; },
    });
    let shownLang = prefs().language;
    onPrefs((p) => {
      // A new language rebuilds the menus around the page.
      if (p.language !== shownLang) {
        shownLang = p.language;
        if (shell) { shell = null; void route(db); }
        return;
      }
      void counts(db).then((c) => shell?.setCounts(c));
    });
    window.addEventListener("offline", () => toast("You are offline. Changes will not save until the connection is back.", "bad", 6000));
    window.addEventListener("online", () => toast("Back online"));
    await route(db);
    // After the first page, so the initial "signed in" event does not draw it twice.
    db.onAuthChange((e) => void onAuth(db, e.byChoice));
  } catch (e) {
    mount(app, notice("error", e instanceof Error ? e.message : String(e)));
  }
}

void start();
